import { Mic, Pause, Play, Square, Trash2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

interface AudioRecorderProps {
  onAudioChange: (audio: File | null) => void;
}

// Get the best supported audio MIME type for the current browser
const getSupportedMimeType = (): { mimeType: string; extension: string } => {
  // Check various formats - Safari needs special handling
  const types = [
    // Safari on iOS/macOS - try mp4 with aac codec first
    { mimeType: "audio/mp4;codecs=mp4a.40.2", extension: "m4a" },
    { mimeType: "audio/aac", extension: "aac" },
    { mimeType: "audio/mp4", extension: "m4a" },
    // Chrome/Firefox/Edge
    { mimeType: "audio/webm;codecs=opus", extension: "webm" },
    { mimeType: "audio/webm", extension: "webm" },
    { mimeType: "audio/ogg;codecs=opus", extension: "ogg" },
  ];

  for (const type of types) {
    try {
      if (MediaRecorder.isTypeSupported(type.mimeType)) {
        console.log(`Audio format supported: ${type.mimeType}`);
        return type;
      }
    } catch (e) {
      // Some browsers throw on isTypeSupported for unknown types
      console.log(`Error checking ${type.mimeType}:`, e);
    }
  }

  // Fallback - let browser decide (Safari often works best this way)
  console.log("No specific format supported, using browser default");
  return { mimeType: "", extension: "m4a" };
};

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onAudioChange }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioFormatRef = useRef<{ mimeType: string; extension: string }>({ mimeType: "", extension: "audio" });

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Detect the best supported audio format for this browser
      const format = getSupportedMimeType();
      audioFormatRef.current = format;
      console.log("Using audio format:", format);

      // Create MediaRecorder with detected format (or let browser choose if empty)
      const mediaRecorder = format.mimeType
        ? new MediaRecorder(stream, { mimeType: format.mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use the actual MIME type from MediaRecorder (which may differ from requested)
        let actualMimeType = mediaRecorder.mimeType || audioFormatRef.current.mimeType;
        let extension = audioFormatRef.current.extension;

        // Safari often returns empty mimeType, detect from user agent
        if (!actualMimeType) {
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          if (isSafari) {
            actualMimeType = "audio/mp4";
            extension = "m4a";
          } else {
            actualMimeType = "audio/webm";
            extension = "webm";
          }
        }

        console.log("Recording stopped. Actual MIME type:", actualMimeType, "Extension:", extension);

        const audioBlob = new Blob(audioChunksRef.current, {
          type: actualMimeType,
        });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Convert to File object with correct extension
        const file = new File([audioBlob], `voice-note-${Date.now()}.${extension}`, {
          type: actualMimeType,
        });
        onAudioChange(file);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Unable to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    onAudioChange(null);
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        Voice Note
      </label>

      {!audioUrl ? (
        <div className="flex items-center space-x-4">
          {!isRecording ? (
            <button
              type="button"
              onClick={startRecording}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Mic className="h-5 w-5 mr-2" />
              Start Recording
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={stopRecording}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Square className="h-5 w-5 mr-2" />
                Stop
              </button>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">
                  Recording: {formatTime(recordingTime)}
                </span>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={togglePlayback}
                className="p-2 rounded-full bg-green-100 text-green-600 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5" />
                ) : (
                  <Play className="h-5 w-5" />
                )}
              </button>
              <div>
                <p className="text-sm font-medium text-gray-900">Voice Note</p>
                <p className="text-sm text-gray-500">
                  Duration: {formatTime(recordingTime)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={deleteRecording}
              className="p-2 text-red-600 hover:bg-red-50 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
